import { BigNumber } from '@xylabs/bignumber'
import { assertEx, EthAddress } from '@xylabs/sdk-js'
// eslint-disable-next-line import/no-internal-modules
import { XyoWorldMinterEvents } from '@xyo-network/world-typechain'

import { XyoWorldBase } from '../Base'
import { XyoWorldGeotokenWrapper } from '../EthersClients'
import { BaseJsonObject } from '../models'
import { Quadkey } from '../Quadkey'
import { MinterEvents, XyoWorldMinter } from './Minter'

interface JsonObject extends BaseJsonObject {
  address: string
}

export class XyoWorldBidder extends XyoWorldBase<JsonObject> {
  public address: EthAddress

  private _balance?: BigNumber

  private _bids?: XyoWorldMinterEvents.BidPlaced[]

  private _events?: MinterEvents

  private minter: XyoWorldMinter

  constructor(address: EthAddress, minter: XyoWorldMinter, jsonObject?: JsonObject) {
    super()
    this.minter = minter
    this.address = address
    this.loadJsonObject(jsonObject)
  }

  public events() {
    this._events = this._events || new MinterEvents(this.minter.contracts, { bidder: this.address })
    return this._events
  }

  public async fetchAuctionsBidOn() {
    await this.fetchQuadkeysBidOn()
    return this.getAuctionsBidOn()
  }

  public async fetchBalance() {
    this._balance = await (await this.minter.currency())?.getBalance(this.address)
    return this._balance
  }

  public async fetchQuadkeysBidOn() {
    return await this.getQuadkeysBidOn()
  }

  public async getAllowance() {
    return (await this.minter.currency())?.getAllowance(assertEx(this.minter.address(), 'Missing Minter Address'), this.address)
  }

  public async getAuctionsBidOn() {
    const quadkeys = await this.getQuadkeysBidOn()
    return quadkeys.map((quadkey) => this.minter.auctions().get(quadkey))
  }

  public async getBalance() {
    return this._balance ?? (await this.fetchBalance())
  }

  public async getBids() {
    return this._bids ?? (await this.events()?.bidPlaced())
  }

  public async getMaxCurrency() {
    return (await this.minter.currency())?.getMaxBalance(this.address)
  }

  public async getOwnedGeotokens() {
    const geotokens = await this.minter.geotokens()
    if (geotokens) {
      const quadkeys = await geotokens?.getQuadkeysByOwner(this.address)
      return quadkeys.map((quadkey) => XyoWorldGeotokenWrapper.get(geotokens, quadkey))
    }
  }

  public async getQuadkeysBidOn() {
    const set = new Set<Quadkey>()
    const bidHistory = await this.getBids()
    for (const bid of bidHistory) {
      const quadkey = Quadkey.fromBase10String(bid.args.id?.toString())
      if (quadkey) {
        set.add(quadkey)
      }
    }

    return Array.from(set)
  }

  public async getTotalCurrencyInOwnedGeotokens() {
    const geotokens = await this.getOwnedGeotokens()
    const currencyAddress = (await this.minter.currency())?.getAddress()
    let total = new BigNumber(0)
    if (geotokens && currencyAddress) {
      for (const geotoken of geotokens) {
        total = total.add(new BigNumber((await geotoken.getErc20Balance(currencyAddress)).toString()) ?? 0)
      }
    }
    return total
  }

  loadJsonObject(jsonObject?: JsonObject) {
    if (jsonObject) {
      this.address = assertEx(EthAddress.fromString(jsonObject.address), 'Bad address')
    }
    return this
  }

  toJsonObject(): JsonObject {
    return {
      address: this.address.toString(),
    }
  }
}
